/**
 * cassettoFiscaleSpec — `cassetto_fiscale` capability (Acube only today).
 *
 * Per CLAUDE.md "white-label naming OBBLIGATORIO": vendor name `acube`
 * MUST NOT appear in URL/FE/error message. BE-side `ConnectorFactory`
 * resolves to the Acube driver.
 *
 * v1 source-of-truth: none yet (Aruba Cassetto, Acube Cassetto = roadmap).
 */

import type { BaseConnection, CapabilityWizardSpec } from "@ando/ui";
import { i18n as i18nSingleton } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { Archive, Building2, Shield, Stamp } from "lucide-react";

import { createCapabilityApi } from "../api/capabilityApi";

export interface CassettoConnection extends BaseConnection {
  readonly vat_number?: string;
  readonly fiscal_code?: string;
  readonly delegation_status?: "pending" | "active" | "expired";
}

const iconCls = "h-4 w-4";

export const cassettoFiscaleSpec: CapabilityWizardSpec<CassettoConnection> = {
  capability: "cassetto_fiscale",
  title: msg`Cassetto Fiscale`,
  subtitle: msg`Accesso delegato al cassetto fiscale Agenzia delle Entrate`,
  icon: <Archive className="h-5 w-5" aria-hidden />,
  gdprDeleteWindowMinutes: 30,

  vendors: [
    {
      id: "cassetto_fiscale",
      label: msg`Cassetto Fiscale`,
      icon: <Archive className={iconCls} aria-hidden />,
      whiteLabel: true,
    },
  ],

  steps: [
    {
      id: "delegation",
      kind: "form",
      title: msg`Delega`,
      description: msg`Inserisci P.IVA e codice fiscale del soggetto delegante.`,
      fields: [
        { name: "vat_number", label: msg`Partita IVA`, type: "string", required: true },
        { name: "fiscal_code", label: msg`Codice fiscale`, type: "string", required: true },
      ],
    },
    {
      id: "authorize",
      kind: "oauth",
      title: msg`Firma delega`,
      description: msg`Verrai reindirizzato al portale AdE per la firma SPID/CIE.`,
      oauth: { buttonLabel: msg`Procedi alla firma` },
    },
  ],

  benefits: [
    {
      icon: <Stamp className={iconCls} aria-hidden />,
      title: msg`F24 + cartelle`,
      body: msg`Stato pagamenti, cartelle, avvisi bonari direttamente in chat.`,
    },
    {
      icon: <Archive className={iconCls} aria-hidden />,
      title: msg`Storico completo`,
      body: msg`Dichiarazioni, registri, comunicazioni — un solo posto.`,
    },
    {
      icon: <Building2 className={iconCls} aria-hidden />,
      title: msg`Multi-azienda`,
      body: msg`Una delega per ogni P.IVA che gestisci.`,
    },
    {
      icon: <Shield className={iconCls} aria-hidden />,
      title: msg`Audit trail`,
      body: msg`Ogni accesso loggato + esportabile per il commercialista.`,
    },
  ],

  api: createCapabilityApi<CassettoConnection>({
    capability: "cassetto_fiscale",
    defaultVendor: "cassetto_fiscale",
  }),
  connectionKeyField: "id" as keyof CassettoConnection,
  getDisplayName: (c) => c.vat_number ?? c.id ?? i18nSingleton._(msg`Cassetto`),
  // ``delegation_status`` is surfaced as a coloured chip in <ConnectedAccountRow>
  // via ``metadata.delegation_status`` (DRY across every delegated-auth flow).
  // The secondary line keeps the more durable identifier — the fiscal code —
  // when present, falling back to the VAT.
  getSecondaryId: (c) => c.fiscal_code ?? c.vat_number ?? null,
};
